import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <div className="flex-1 hidden md:flex flex-col justify-between p-12 border-r border-[#2A2A2A] relative overflow-hidden">
        <div>
          <div className="font-heading text-4xl font-bold tracking-tighter">
            CO<span className="text-[#E1FF00]">/</span>LD
          </div>
          <div className="micro mt-1">Sending Command Center</div>
        </div>
        <div className="space-y-6 max-w-md">
          <h1 className="font-heading text-5xl leading-tight font-bold tracking-tighter">
            One mailbox.<br />
            One recipient.<br />
            <span className="text-[#E1FF00]">One proxy.</span>
          </h1>
          <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-sm">
            Deterministic sequential sending with strict proxy enforcement.
            No direct fallback. No shared routes. Every send is exact.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          [ v1.0 // MVP ]
        </div>
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
             style={{ backgroundImage: "linear-gradient(90deg, #E1FF00 1px, transparent 1px), linear-gradient(0deg, #E1FF00 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      </div>

      <div className="w-full md:w-[440px] flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full space-y-6">
          <div>
            <div className="micro mb-2">Authenticate</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          </div>

          <div className="space-y-4">
            <Field label="Email">
              <input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2.5 text-sm text-white focus:border-[#E1FF00] focus:outline-none focus:ring-1 focus:ring-[#E1FF00]"
              />
            </Field>
            <Field label="Password">
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2.5 text-sm text-white focus:border-[#E1FF00] focus:outline-none focus:ring-1 focus:ring-[#E1FF00]"
              />
            </Field>
          </div>

          {error && (
            <div data-testid="login-error" className="text-xs font-mono text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#E1FF00] text-black font-semibold text-sm py-3 hover:bg-[#CCEE00] transition-colors disabled:opacity-50"
          >
            {loading ? "AUTHENTICATING..." : "SIGN IN"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
